import { Box, Group, Stack, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';
import { CaptureActions } from '../components/CaptureActions.js';
import { CompanySwitcher } from '../components/CompanySwitcher.js';
import { OfflineQueueNotice } from '../components/OfflineQueueNotice.js';
import { useT } from '../i18n/useT.js';

/** Where the installed app opens. The manifest's `start_url` and its shortcuts name it. */
export const MOBILE_APP_PATH = '/app';

/**
 * The installed app: two buttons and the company they save to.
 *
 * ## Outside AppLayout, on purpose
 *
 * No sidebar, no account menu, no bookkeeping screens. What a phone is for here
 * is photographing a receipt and logging a trip, and the full UI on a 6-inch
 * screen is a burger menu over thirteen pages nobody opens standing in a car
 * park. The full system is still the same site in the browser.
 *
 * ## The company switcher is the one piece of chrome that stays
 *
 * A receipt uploaded here lands in whichever company is selected, and there is
 * no deleting a linked attachment afterwards. With two companies on one phone,
 * a screen that hid which one was active would be how a receipt ends up in the
 * wrong set of books — the same reason AppLayout keeps the switcher in the
 * header rather than the collapsible navbar.
 *
 * ## `/app`, and why that is safe
 *
 * Traefik routes `/v1`, `/api/auth`, `/health`, `/ready`, `/webhooks` and `/mcp`
 * away from this SPA by raw string prefix (infrastructure/traefik/accountyob.yml).
 * `/app` matches none of them — `/api/auth` needs the `i`.
 */
export function MobileAppPage(): ReactNode {
  const t = useT();

  return (
    <Box
      component="main"
      mih="100dvh"
      p="md"
      style={{
        // The manifest asks for standalone display and index.html for
        // viewport-fit=cover, so the status bar overlaps the page unless the
        // inset is honoured.
        paddingTop: 'max(var(--mantine-spacing-md), env(safe-area-inset-top))',
        paddingBottom: 'max(var(--mantine-spacing-md), env(safe-area-inset-bottom))',
      }}
    >
      <Stack gap="lg" maw={560} mx="auto">
        <Group justify="space-between" wrap="nowrap">
          <Title order={1} size="h4">
            {t('app.name')}
          </Title>
          <CompanySwitcher />
        </Group>

        <Text size="sm" c="dimmed">
          {t('mobile.companyHint')}
        </Text>

        <OfflineQueueNotice />

        <CaptureActions />
      </Stack>
    </Box>
  );
}
